import mismaClave from './mismaClave.js';
import { comoPromesaComun, esCancelacion } from './sesionDePeticiones.js';

/**
 * Un carril de peticiones: **decide él quién contesta**, para que no lo decida cada `then` y cada
 * `catch` por su cuenta.
 *
 * Las pantallas del módulo de festivos (#53) piden lo mismo varias veces seguidas —las flechas de
 * año lanzan una petición por pulsación, cambiar de persona lanza otra— y las respuestas no
 * vuelven en orden. Sin esto, la respuesta vieja llegaba después y pintaba los datos de otro año o
 * de otra persona encima de los buenos.
 *
 * Cada pantalla llevaba su contador y un `if (peticion !== self.peticion) return;` repetido en el
 * `then` y en el `catch` de cada carga. Repetido es olvidable: el `catch` de la tarjeta del
 * domicilio se dejaba sin limpiar lo que la carga fallida había invalidado, y la tarjeta se
 * quedaba inservible hasta salir y volver a entrar (revisión CODEX del PR #1709). Aquí el token va
 * en un solo sitio y el `limpiar` **se llama solo** cuando falla la petición vigente.
 *
 *   const carril = carrilPeticiones();
 *   carril.lanzar(Festivo.municipios(anio), {
 *     exito: function (res) { ... },
 *     limpiar: function () { ... },   // el estado que dependía de esta carga, al fallar
 *     fallo: function (e) { ... },
 *     descartada: function (ok) { ... }   // su respuesta no decide: ni pinta ni limpia (ver abajo)
 *   });
 *
 * `invalidar()` es para lo que manda sobre cualquier carga en vuelo: después de guardar, la
 * respuesta del guardado es la verdad y las cargas anteriores ya no pintan.
 *
 * `descartada` es el aviso de que una respuesta no va a escribir en la pantalla, con `ok` a true si
 * fue un éxito. Casi nadie lo necesita: descartar una LECTURA no deja nada pendiente, porque la
 * lectura vigente pinta lo bueno. Lo necesita quien descarta una ESCRITURA, que sí ha cambiado el
 * servidor aunque su respuesta ya no valga para pintar: ahí lo que hay delante es anterior al
 * cambio y hay que releerlo (revisión CODEX del PR #1725).
 *
 * ## Las dos políticas
 *
 * - **Por omisión manda la última LANZADA.** Lo que se pide ahora ha dejado sin interés a lo de
 *   antes —otro año, otra persona, otra ventana—, así que cualquier respuesta anterior llega fuera
 *   de turno y no cuenta para nada.
 *
 * - **Con `{ acumulativo: true }` manda la última APLICADA.** Todas las peticiones del carril
 *   piden LO MISMO, así que una que falla no invalida a las que siguen en vuelo: cualquiera de
 *   ellas puede traer la respuesta buena. Es el caso de los sondeos del historial de boletines
 *   (#262), donde el sondeo que falla en la misma conexión mala que provocó el corte no puede
 *   tirar la respuesta que viene en camino.
 *
 * Y lo que el modo acumulativo añade, que es justo lo que no se puede repetir pantalla a pantalla
 * sin que se olvide en algún sitio: **el fallo no se declara hasta que han fallado todas las
 * peticiones que podían contestar**. Llevando la cuenta a mano, el fallo de la última lanzada
 * ponía «no se ha podido cargar» —y su alerta— mientras una anterior seguía viva y acabó cargando
 * la lista bien (revisión CODEX del PR #1759, ronda 3). Mientras quede alguna, el `catch` de la
 * que falla pasa por `descartada` y la pantalla se queda como está: cargando.
 *
 * `enVuelo()` dice cuántas peticiones de la sesión vigente no han contestado todavía. Es para los
 * botones que se apagan mientras se refresca: con varias en vuelo, la primera que vuelve no es el
 * final del refresco.
 *
 * ## La clave: de qué es esta carga
 *
 * Las dos políticas saben de **orden**, no de contenido. Eso deja un hueco, y es el que destapó la
 * revisión de la #59 en el calendario: si la pantalla pasa a mirar otra cosa **sin lanzar nada por
 * este carril**, la respuesta en vuelo sigue siendo «la última» y se escribe encima. En el
 * calendario sobra con que el cambio de empleado se vaya por una de las salidas de
 * `getPlanificaciones` que navegan y no llegan a pedir la agenda: la respuesta del empleado
 * anterior aterriza sobre el recién elegido, y en pantalla queda el nombre de una persona con los
 * días de otra —sin ningún aviso, en una herramienta con la que se deciden vacaciones—.
 *
 * Así que una carga puede declarar **de qué es** (`clave`) y **cómo se pregunta por lo que se
 * mira ahora** (`deAhora`, que se consulta cuando la respuesta llega, no cuando se lanza):
 *
 *   const clave = self.claveDelCalendario();           // [empleado, desde, hasta]
 *   carril.lanzar(Planificacion.getList({ ... }), {
 *     clave: clave,
 *     deAhora: self.claveDelCalendario,
 *     exito: function (res) { ... },
 *     limpiar: function () { ... },
 *     fallo: function (e) { ... }
 *   });
 *
 * Una respuesta se escribe si le toca por la política del carril **y** es de lo que se está
 * mirando. Las dos condiciones hacen falta: el orden descarta la petición adelantada por otra del
 * mismo carril, y la clave descarta la que ya no va de lo que hay en pantalla aunque nadie haya
 * vuelto a pedir nada. Sin `clave` el carril se comporta exactamente como si no existiera, que es
 * lo que siguen haciendo las pantallas que no la necesitan.
 *
 * Y una respuesta descartada **no limpia ni avisa del error**: su `limpiar` borraría el estado de
 * la carga buena y su diálogo culparía al empleado que ahora se mira de un fallo que era del
 * anterior; lo único que recibe es `descartada`.
 *
 * ## El sello de la sesión
 *
 * `sesion(sello)` declara con qué sesión se está pidiendo. Cambiarlo —cerrar sesión y entrar con
 * otra cuenta, o la misma después de volver a identificarse— **invalida de golpe todo lo que esté
 * en vuelo**: ninguna de esas peticiones puede ya aplicar datos, ni declarar un fallo, ni contar
 * como «todavía cargando». Su respuesta, cuando llegue, pasa por `descartada`.
 *
 * Esto vive AQUÍ y no en cada pantalla a propósito (#262, decisión de dirección del 06/10/2026
 * tras cuatro rondas de revisión del PR #1759). La pantalla de boletines llevaba su propia guarda
 * de sesión al lado del carril, y eran dos vigilantes que no se hablaban: el carril consideraba
 * que una petición de la cuenta anterior «todavía podía contestar», así que se tragaba el fallo
 * del primer GET de la cuenta nueva y mantenía la pantalla en «cargando» con el botón de
 * actualizar apagado —para siempre si aquella petición no terminaba nunca—; y cuando terminaba, la
 * guarda de la pantalla la descartaba sin devolver el estado a su sitio. Con una sola decisión no
 * hay juntura donde equivocarse.
 *
 * El sello es OPACO: el carril solo lo compara con el anterior para saber si ha cambiado. No lo
 * interpreta, no lo registra y no lo entrega a ningún manejador, así que puede ser un contador
 * —lo que usa la pantalla de boletines— sin que haga falta meterle dentro el usuario ni el token.
 * Quien no llama a `sesion` no nota nada: el sello se queda indefinido y todo se compara igual.
 *
 * ## La promesa que devuelve `lanzar` se cumple SIEMPRE que la carga termina
 *
 * Quien la espera no espera datos —esos los escribe `exito`—: espera a que la carga haya
 * terminado, para apagar un «cargando», esconder un preloader o cerrar su propio promise. Así que
 * **no se rechaza nunca**, y en particular tampoco cuando lo que se rompe es un manejador.
 *
 * Eso es lo que destapó la revisión del PR #1735: `promesa.then(exito, fallo)` solo contesta a la
 * petición, no a los manejadores, y una excepción DENTRO de `exito` —la respuesta llega bien y es
 * al transformarla cuando se rompe, un `DESPLAZAMIENTO` antiguo sin `desplazamiento.distance`—
 * dejaba la promesa rechazada. Los cuatro sitios que la esperan la esperaban solo por el
 * cumplimiento, así que se quedaban colgados para siempre: el calendario sin saber de quién es la
 * rejilla —y con él el botón VACACIONES y el detalle del día—, «Mis vacaciones» con su «cargando»
 * puesto y su preloader tapando la pantalla entera. Sin aviso de nada, que es lo peor: antes del
 * carril, el `.catch` que había después del `then` sí recogía esa excepción y la contaba.
 *
 * El carril lo cierra por los dos lados, y aquí y no en cada pantalla por lo mismo que el resto:
 * cuatro `then` con su manejador de rechazo son cuatro sitios donde olvidarlo, y el quinto que se
 * escriba mañana nacería colgado.
 *
 * - **`exito` que se rompe es una carga fallida**: se limpia y se avisa, como si la petición no
 *   hubiera llegado. No hay opción de dejarlo como está: la excepción ha parado a `exito` a mitad,
 *   así que lo que hay en pantalla está escrito a medias, y ninguna respuesta posterior va a
 *   deshacer eso. Es también lo que hacía el `.catch` de antes.
 * - **`limpiar`, `fallo` o `descartada` que se rompen** no se llevan por delante al que venga
 *   detrás —`limpiar` roto dejaba sin avisar del error— ni a la promesa de la carga. Ahí ya no
 *   queda nada que hacer por la pantalla, así que se registran en la consola.
 *
 * ## Lo que se ve MIENTRAS: vaciar lo de antes y el «cargando» (#472)
 *
 * Todo lo de arriba decide qué hace una respuesta **cuando llega**. Quedaban dos cosas del rato de
 * en medio, y las dos dependían de cada pantalla y de cada sitio donde se cambia algo:
 *
 * - **tirar lo que había** de la elección anterior. El título cambia al pulsar y las cifras siguen
 *   siendo las de antes hasta que contesta el servidor: «Solicitadas sin aprobar de 2025» con los
 *   datos de 2026. Cada entrada nueva —el año, el empleado, la cuenta, las fechas— necesitaba que
 *   alguien se acordara de vaciar, y salió una por ronda en la revisión del PR #1733;
 * - **cuándo se apaga el «cargando»**. Colgado del `then` de la promesa de `lanzar`, que se cumple
 *   también para las respuestas descartadas, la del año anterior lo apagaba con la vigente todavía
 *   en vuelo y la mezcla se quedaba a la vista como si fuera definitiva.
 *
 * Dirección decidió el 09/10/2026 en la #58 que las dos vivieran aquí, como el sello de sesión, y
 * para eso son las tres opciones del carril:
 *
 *   const carril = carrilPeticiones({
 *     deAhora: function () { return [self.anio]; },   // de qué es lo que se está mirando
 *     vaciar: function () { self.resumen = null; },    // lo que se tira al pedir OTRA cosa
 *     cargando: function (si) { self.cargando = si; }  // el indicador, encendido y apagado aquí
 *   });
 *   carril.lanzar(Resumen.get(self.anio), { exito: ..., limpiar: ..., fallo: ... });
 *
 * - **`deAhora`** es el mismo de cada `lanzar`, declarado una vez para todo el carril: una carga
 *   que no diga su `clave` se sella con lo que devuelva en el momento de lanzarse. Las dos cosas se
 *   pueden seguir dando en cada `lanzar`, y entonces mandan las de la carga.
 * - **`vaciar` se llama al lanzar una carga de algo distinto de lo que hay pintado**, antes de que
 *   la pantalla vuelva a dibujarse: lo que se ve hasta que llega la respuesta es vacío y no lo de
 *   antes. «Lo pintado» es la clave de la última respuesta aplicada, preguntada a `deAhora`
 *   justo después de aplicarla —la primera carga de «Mis vacaciones» no sabe el año hasta que lo
 *   dice el servidor—. Recargar lo mismo **no vacía**: ahí las cifras son las de hace un segundo,
 *   del mismo año, y vaciarlas sería un parpadeo. Las claves se comparan aquí valor a valor y con
 *   `===`, también los `null`: para decidir si hay que tirar algo, «no se sabía» no es «es lo
 *   mismo». También vale en cada `lanzar`, para la carga que tire algo distinto.
 * - **`cargando(true)` se llama en cada `lanzar` y `cargando(false)` cuando ya no queda ninguna
 *   petición cuya respuesta pueda escribir**: la vigente ha contestado —bien, mal o rompiéndose—,
 *   o `invalidar()` o un sello de sesión nuevo han dejado sin voz a todas. Una respuesta
 *   descartada no lo apaga nunca, y un `false` llega una vez, después del manejador de la
 *   respuesta, con la pantalla ya escrita. El `true` repetido es a propósito: el indicador de
 *   espera de la aplicación (`preloaderDeLaSesion`) se suelta solo al salir de la pantalla, y la
 *   carga siguiente tiene que poder encenderlo otra vez.
 *
 * Con eso, la pantalla dice una vez qué mira, qué tira y qué indicador lleva, y ningún sitio que
 * cambie la elección ni ningún `then` tiene que acordarse de nada.
 */
export default function carrilPeticiones(opciones) {
  const acumulativo = !!(opciones && opciones.acumulativo);
  const deAhoraDelCarril = opciones && opciones.deAhora;
  const vaciarDelCarril = opciones && opciones.vaciar;
  const indicador = opciones && opciones.cargando;
  let ultima = 0;
  let aplicada = 0;
  const sinContestar = [];
  // La clave de lo que hay pintado ahora, o `NADA` si no hay nada que tirar: ni ha llegado ninguna
  // respuesta todavía ni se ha vaciado después de la última. Es con lo que se decide `vaciar`.
  const NADA = {};
  let pintada = NADA;
  // Si el último aviso de `cargando` fue un `true`: el `false` se da una vez por carga terminada,
  // no por cada respuesta que llega.
  let cargandoAvisado = false;
  // El último sello visto, y el número de veces que ha cambiado. Lo que se compara es el número:
  // el sello solo se mira al declararlo, y así un sello que se repitiera —no pasa hoy, el token va
  // dentro del de la pantalla de boletines— tampoco devolvería la vida a las peticiones de antes.
  let sello;
  let vigencia = 0;

  function olvidar(mia) {
    const indice = sinContestar.indexOf(mia);
    if (indice !== -1) sinContestar.splice(indice, 1);
  }

  // ¿Sigue siendo de la sesión de ahora? Lo primero que se pregunta: una petición de la sesión
  // anterior no decide nada, ni para bien ni para mal.
  function deLaSesion(mia) {
    return mia.vigencia === vigencia;
  }

  // ¿Va esta carga de lo que se está mirando? Se pregunta ahora y no al lanzarla: lo que se mira es
  // justo lo que ha podido cambiar mientras la petición estaba en vuelo.
  function deLoQueSeMira(mia) {
    if (!mia.deAhora) return true;
    return mismaClave(mia.clave, mia.deAhora());
  }

  // ¿Escribe esta respuesta en la pantalla?
  function decide(mia) {
    if (!deLaSesion(mia) || !deLoQueSeMira(mia)) return false;
    return acumulativo ? mia.numero > aplicada : mia.numero === ultima;
  }

  // ¿Queda alguna petición sin contestar cuya respuesta sí escribiría? En el modo por omisión no
  // puede quedar ninguna —solo decide `ultima`, y es la que acaba de contestar—, así que esto solo
  // cambia algo en el acumulativo.
  function quedaQuienPuedaDecidir() {
    return sinContestar.some(function (suya) { return decide(suya); });
  }

  // ¿Puede escribir todavía alguna de las que no han contestado? Es la pregunta del «cargando», y
  // no es la de `enVuelo()`: una petición adelantada por otra sigue en vuelo, pero ya no va a
  // escribir nada y esperarla sería dejar el indicador puesto sobre una pantalla terminada. La clave
  // no se mira: de qué es lo que se mira se pregunta al llegar, y hasta entonces la vigente lo es.
  function quedaPorContestarLaVigente() {
    return sinContestar.some(function (suya) {
      return deLaSesion(suya) && (acumulativo ? suya.numero > aplicada : suya.numero === ultima);
    });
  }

  // El `false` del indicador, si toca. Se pregunta después de cada respuesta —con su manejador ya
  // corrido, así que lo que queda delante es lo definitivo— y después de lo que deja sin voz a las
  // que estaban en vuelo: `invalidar()` y un sello de sesión nuevo.
  function repasarCargando() {
    if (!cargandoAvisado || quedaPorContestarLaVigente()) return;
    cargandoAvisado = false;
    avisar(indicador, 'cargando', false);
  }

  // ¿Es la misma elección? Valor a valor y con `===`, también los `null`. No es `mismaClave`, y a
  // propósito: allí un `null` de la pantalla es «no se sabe todavía» y deja pasar la respuesta, que
  // es lo que hace falta para no descartar la primera carga; aquí decidiría NO vaciar al pasar de
  // «no se sabe» a un año concreto, que es justo cuando hay algo que tirar.
  function mismaEleccion(una, otra) {
    const unas = [].concat(una);
    const otras = [].concat(otra);
    if (unas.length !== otras.length) return false;
    for (let i = 0; i < unas.length; i++) {
      if (unas[i] !== otras[i]) return false;
    }
    return true;
  }

  // Los manejadores de una carga con lo declarado para todo el carril. Una carga que dice de qué es
  // —con su `clave` o su `deAhora`— no hereda nada de eso: es lo que ya hacían las pantallas antes
  // de que el carril lo supiera, y tiene que seguir significando lo mismo.
  function conLoDelCarril(suyos) {
    const al = Object.assign({}, suyos);
    if (deAhoraDelCarril && al.clave === undefined && al.deAhora === undefined) {
      al.deAhora = deAhoraDelCarril;
      // Copiada, no la lista que devuelva la pantalla: si fuera suya y la cambiara después, la
      // carga pasaría a ser de lo que se mira ahora y no de lo que se pidió.
      al.clave = [].concat(deAhoraDelCarril());
    }
    if (al.vaciar === undefined) al.vaciar = vaciarDelCarril;
    return al;
  }

  // De qué es lo que queda pintado después de aplicar una respuesta. Se pregunta a `deAhora` si lo
  // hay, y no a la clave con la que se pidió: la primera carga puede no saber de qué es hasta que
  // contesta el servidor, y la que tira lo de antes al cambiar es la siguiente.
  function loQueQuedaPintado(mia) {
    return [].concat(mia.deAhora ? mia.deAhora() : mia.clave);
  }

  // Llama a un manejador sin que lo que haga dentro pueda romper el carril: ni dejar sin llamar al
  // siguiente, ni dejar sin cerrar la promesa de la carga. No se traga el aviso —queda en la
  // consola con su traza—, porque una excepción aquí es un defecto de la pantalla, no un fallo de
  // red: el único sitio donde se puede ver es ese.
  function avisar(quien, nombre, dato, mas) {
    if (!quien) return;
    try {
      quien(dato, mas);
    } catch (e) {
      console.error('carrilPeticiones: el manejador `' + nombre + '` ha lanzado', e);
    }
  }

  // El final en fallo de una carga, venga de la petición o de un `exito` que se ha roto a mitad.
  // Primero limpiar y después avisar: el diálogo del error es lo último que se ve, y la pantalla
  // que queda detrás no puede seguir enseñando lo de antes de la acción.
  function caida(al, e) {
    avisar(al.limpiar, 'limpiar');
    avisar(al.fallo, 'fallo', e);
  }

  return {
    lanzar: function (promesa, manejadores) {
      const al = conLoDelCarril(manejadores || {});
      // Lo de antes se tira AQUÍ, en la misma llamada que cambia lo que se pide y antes de que la
      // pantalla vuelva a dibujarse: entre pulsar y que conteste el servidor no se ve ni un
      // instante la cifra anterior bajo el título nuevo.
      if (al.vaciar && pintada !== NADA && !mismaEleccion(pintada, al.clave)) {
        pintada = NADA;
        avisar(al.vaciar, 'vaciar');
      }
      const mia = {
        numero: ++ultima, vigencia: vigencia, clave: al.clave, deAhora: al.deAhora
      };
      sinContestar.push(mia);
      cargandoAvisado = true;
      avisar(indicador, 'cargando', true);
      // #663 · Adoptada como promesa común: si la sesión cambia mientras espera, la frontera calla
      // las continuaciones, y sin estas la carga no se daba de baja y el indicador del carril se
      // quedaba encendido para siempre.
      return comoPromesaComun(promesa).then(
        function (res) {
          olvidar(mia);
          if (!decide(mia)) {
            avisar(al.descartada, 'descartada', true, res);
            return;
          }
          aplicada = mia.numero;
          try {
            al.exito(res);
            pintada = loQueQuedaPintado(mia);
          } catch (e) {
            // La respuesta llegó bien y es `exito` el que se ha roto transformándola: para la
            // pantalla es lo mismo que si la petición hubiera fallado, y además peor, porque lo
            // que haya escrito está a medias. Se cuenta por donde se cuentan los fallos.
            //
            // Y además en la consola: por `fallo` viaja el mensaje, que es lo que se le enseña a
            // quien está delante, pero no la traza, que es lo único con lo que se arregla un
            // defecto de este lado.
            console.error('carrilPeticiones: `exito` se ha roto al aplicar la respuesta', e);
            caida(al, e);
          }
        },
        function (e) {
          olvidar(mia);
          // La carga de otra sesión no decide ni se descarta: `descartada` puede avisar o releer,
          // y no hay delante nadie a quien le toque. Solo se da de baja.
          if (esCancelacion(e)) return;
          // Una petición fallida no adelanta nada: ni aplica ni invalida a nadie. Si su respuesta
          // ya no era la que escribe, o si todavía queda otra que sí escribiría, el fallo no
          // cambia la pantalla.
          if (!decide(mia) || quedaQuienPuedaDecidir()) {
            avisar(al.descartada, 'descartada', false, e);
            return;
          }
          caida(al, e);
        }
      ).then(repasarCargando);
    },

    // Sube el listón de las dos políticas a la vez: en el acumulativo, lo que esté en vuelo deja
    // de poder aplicarse: era de antes de lo que manda ahora.
    invalidar: function () {
      aplicada = ++ultima;
      repasarCargando();
    },

    // Devuelve si el sello ha cambiado, para quien tenga que tirar además lo suyo.
    sesion: function (nuevo) {
      if (vigencia > 0 && nuevo === sello) return false;
      sello = nuevo;
      vigencia += 1;
      repasarCargando();
      return true;
    },

    enVuelo: function () {
      return sinContestar.filter(deLaSesion).length;
    },

    // Si queda alguna petición cuya respuesta todavía pueda escribir: lo mismo que dice el último
    // aviso de `cargando`, para quien tenga que preguntarlo en vez de escucharlo.
    esperando: function () {
      return quedaPorContestarLaVigente();
    }
  };
}
